/** Minimal hash router: pattern matching, mounting and teardown. */

export interface PageContext {
  readonly path: string;
  readonly params: Readonly<Record<string, string>>;
  readonly query: URLSearchParams;
}

export interface PageView {
  readonly element: HTMLElement;
  /** Called when navigating away — cancel in-flight requests, drop listeners. */
  readonly dispose?: () => void;
}

export type PageFactory = (context: PageContext) => PageView;

export interface Route {
  /** e.g. `/notes/:id/edit` */
  readonly pattern: string;
  readonly factory: PageFactory;
}

interface MatchResult {
  readonly params: Record<string, string>;
}

function splitSegments(path: string): readonly string[] {
  return path.split("/").filter((segment) => segment !== "");
}

function matchPattern(pattern: string, path: string): MatchResult | null {
  const patternSegments = splitSegments(pattern);
  const pathSegments = splitSegments(path);

  if (patternSegments.length !== pathSegments.length) {
    return null;
  }

  const params: Record<string, string> = {};
  for (let index = 0; index < patternSegments.length; index += 1) {
    const expected = patternSegments[index] ?? "";
    const actual = pathSegments[index] ?? "";
    if (expected.startsWith(":")) {
      params[expected.slice(1)] = decodeURIComponent(actual);
    } else if (expected !== actual) {
      return null;
    }
  }
  return { params };
}

function readLocation(): { path: string; query: URLSearchParams } {
  const raw = window.location.hash.replace(/^#/, "");
  const separator = raw.indexOf("?");
  if (separator === -1) {
    return { path: raw === "" ? "/" : raw, query: new URLSearchParams() };
  }
  const path = raw.slice(0, separator);
  return { path: path === "" ? "/" : path, query: new URLSearchParams(raw.slice(separator + 1)) };
}

export interface RouterOptions {
  readonly outlet: HTMLElement;
  readonly routes: readonly Route[];
  readonly fallback: PageFactory;
  /** Runs after each successful mount, e.g. to sync nav highlighting. */
  readonly onNavigate?: (context: PageContext) => void;
}

export interface Router {
  readonly start: () => void;
  readonly stop: () => void;
}

export function createRouter(options: RouterOptions): Router {
  let current: PageView | null = null;

  const render = (): void => {
    const { path, query } = readLocation();
    const route = options.routes
      .map((candidate) => ({ candidate, match: matchPattern(candidate.pattern, path) }))
      .find((entry): entry is { candidate: Route; match: MatchResult } => entry.match !== null);

    const context: PageContext = {
      path,
      params: route === undefined ? {} : route.match.params,
      query,
    };

    current?.dispose?.();
    const view = (route === undefined ? options.fallback : route.candidate.factory)(context);
    current = view;

    options.outlet.replaceChildren(view.element);
    window.scrollTo({ top: 0 });
    options.onNavigate?.(context);
  };

  const onHashChange = (): void => {
    render();
  };

  return {
    start(): void {
      window.addEventListener("hashchange", onHashChange);
      if (window.location.hash === "") {
        window.location.hash = "#/";
      }
      render();
    },
    stop(): void {
      window.removeEventListener("hashchange", onHashChange);
      current?.dispose?.();
      current = null;
    },
  };
}

/** Programmatic navigation; re-renders even if the target equals the current hash. */
export function navigate(hashPath: string): void {
  const target = hashPath.startsWith("#") ? hashPath : `#${hashPath}`;
  if (window.location.hash === target) {
    window.dispatchEvent(new HashChangeEvent("hashchange"));
    return;
  }
  window.location.hash = target;
}
